"use client";

import React from "react";
import { Sparkles, Compass, Shield, Smile, LucideIcon } from "lucide-react";

const iconMap: Record<string, LucideIcon> = {
    Sparkles,
    Compass,
    Shield,
    Smile
};

export interface GqWebValueCard {
    iconName: string;
    title: string;
    desc: string;
    footer: string;
}

export interface GqWebValuesBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    values?: GqWebValueCard[];
}

export default function GqWebValuesBlock({
    tag = "05 / CORE VALUES",
    title = "Creative & Brand Philosophy",
    description = "We structured the marketing website content around four key hospitality and design pillars, establishing a cohesive brand language that bridges tech and luxury.",
    values = [
        {
            iconName: "Sparkles",
            title: "EDITORIAL MINIMALISM",
            desc: "Drawn from printed boutique guidebooks. Generous margins, clean typography scales, and warm tones keep screen layouts looking calm and curated.",
            footer: "// EDITORIAL MINIMALISM"
        },
        {
            iconName: "Compass",
            title: "FRICTIONLESS FLOWS",
            desc: "We designed simple paths that let operators calculate their plan pricing, read verified reviews, and launch their guidebook trial in a few taps.",
            footer: "// FRICTIONLESS FLOWS"
        },
        {
            iconName: "Shield",
            title: "VERIFIED AUTHENTICITY",
            desc: "By integrating verified host stories and live guidebook preview links, we built strong conversion trust without relying on generic sales stock.",
            footer: "// HOST TRUST"
        },
        {
            iconName: "Smile",
            title: "TACTILE EXPERIENCE",
            desc: "Warm background tones, elegant borders, and soft transitions convey structures that elevate digital screens to feel as warm as a physical check-in.",
            footer: "// TACTILE STYLE"
        }
    ]
}: GqWebValuesBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-white border-b border-[#EDE6DD] text-[#444] font-sans antialiased w-full">
            <div className="container mx-auto">
                
                {/* Section header */}
                <div className="max-w-2xl mb-16">
                    {tag && (
                        <span className="text-xs font-mono text-[#D4A574] tracking-widest block mb-2 uppercase font-semibold">
                            {tag}
                        </span>
                    )}
                    {title && (
                        <h2 className="text-4xl font-light text-[#1A1A1A] leading-tight">
                            {title}
                        </h2>
                    )}
                    {description && (
                        <p className="text-zinc-500 text-sm mt-3 font-light max-w-lg">
                            {description}
                        </p>
                    )}
                </div>

                {/* Values Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {values && values.map((val, idx) => {
                        const Icon = iconMap[val.iconName] || Sparkles;
                        return (
                            <div key={idx} className="bg-[#FDFBF8] border border-[#EDE6DD] rounded-2xl p-8 flex flex-col justify-between space-y-6 group hover:border-[#D4A574]/30 transition-all duration-500 shadow-xs">
                                <div className="space-y-4">
                                    <div className="p-3 bg-[#D4A574]/10 border border-[#D4A574]/25 rounded-xl w-fit text-[#D4A574]">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-xl font-medium text-[#1A1A1A]">{val.title}</h3>
                                    <p className="text-zinc-650 text-xs leading-relaxed font-light">
                                        {val.desc}
                                    </p>
                                </div>
                                <span className="text-[10px] font-mono text-zinc-450 font-medium">
                                    {val.footer}
                                </span>
                            </div>
                        );
                    })}
                </div>

            </div>
        </section>
    );
}
